/* eslint-disable react-refresh/only-export-components */
import { useState, useEffect, createContext, useContext, useCallback, type ReactNode } from 'react'
import { adminSupabase as supabase, isAdminSupabaseConfigured as isSupabaseConfigured } from '@admin/lib/supabase'
import { type AdminRole, hasPermission as checkPermission, ADMIN_ROLES, ROUTE_PERMISSIONS } from '../lib/permissions'
import { resetAdminData } from '@admin/utils/resetData'

// ── Types ───────────────────────────────────────────────────────────────────

interface AdminUser {
  id: string
  username: string
  role: AdminRole
}

interface AdminAuthContextValue {
  user: AdminUser | null
  role: AdminRole | null
  isSuperAdmin: boolean
  /** Check if the current admin has a specific permission */
  hasPermission: (permission: string) => boolean
  /** Check if the current admin can access a specific route */
  canAccessRoute: (path: string) => boolean
  logout: () => void
}

export const AdminAuthContext = createContext<AdminAuthContextValue>({
  user: null,
  role: null,
  isSuperAdmin: false,
  hasPermission: () => false,
  canAccessRoute: () => false,
  logout: () => {},
})

export function useAdminAuth() {
  return useContext(AdminAuthContext)
}

/**
 * Extended hook that provides session info + permission helpers.
 * Convenience alias with a more descriptive name.
 */
export function useAdminSession() {
  const ctx = useContext(AdminAuthContext)
  return {
    username: ctx.user?.username ?? null,
    role: ctx.role,
    hasPermission: ctx.hasPermission,
    canAccessRoute: ctx.canAccessRoute,
    isSuperAdmin: ctx.isSuperAdmin,
    roleLabel: ctx.role ? ADMIN_ROLES[ctx.role]?.label ?? ctx.role : null,
  }
}

// ── Session Storage ─────────────────────────────────────────────────────────
// Metadata only — auth credentials are managed by Supabase Auth session.

const ADMIN_SESSION_KEY = 'shimjanggil_admin_session'
const ADMIN_LAST_ACTIVITY_KEY = 'shimjanggil_admin_last_activity'
const SESSION_DURATION = 24 * 60 * 60 * 1000    // 24시간 절대 만료
const IDLE_TIMEOUT = 4 * 60 * 60 * 1000          // 4시간 유휴 타임아웃
const EXPIRY_CHECK_INTERVAL = 60 * 1000         // 1분 간격 만료 체크
const EXPIRY_WARNING_BEFORE = 10 * 60 * 1000    // 만료 10분 전 경고

// ── Brute Force Protection (제거됨 — 잠금 없이 즉시 재시도 가능) ────────────

// ── Session metadata (NOT auth credentials) ─────────────────────────────────

interface AdminSessionMeta {
  displayName: string
  role: AdminRole
  loginAt: string
  expiresAt: string
}

function getStoredSessionMeta(): AdminSessionMeta | null {
  try {
    const raw = localStorage.getItem(ADMIN_SESSION_KEY)
    if (!raw) return null
    const meta: AdminSessionMeta = JSON.parse(raw)
    // 절대 만료 체크
    if (new Date(meta.expiresAt).getTime() < Date.now()) {
      localStorage.removeItem(ADMIN_SESSION_KEY)
      localStorage.removeItem(ADMIN_LAST_ACTIVITY_KEY)
      return null
    }
    // 유휴 타임아웃 체크
    const lastActivity = localStorage.getItem(ADMIN_LAST_ACTIVITY_KEY)
    if (lastActivity && Date.now() - Number(lastActivity) > IDLE_TIMEOUT) {
      localStorage.removeItem(ADMIN_SESSION_KEY)
      localStorage.removeItem(ADMIN_LAST_ACTIVITY_KEY)
      return null
    }
    return meta
  } catch {
    return null
  }
}

function saveSession(displayName: string, role: AdminRole) {
  const meta: AdminSessionMeta = {
    displayName,
    role,
    loginAt: new Date().toISOString(),
    expiresAt: new Date(Date.now() + SESSION_DURATION).toISOString(),
  }
  localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(meta))
  localStorage.setItem(ADMIN_LAST_ACTIVITY_KEY, String(Date.now()))
}

function clearSession() {
  localStorage.removeItem(ADMIN_SESSION_KEY)
  localStorage.removeItem(ADMIN_LAST_ACTIVITY_KEY)
}

function updateLastActivity() {
  localStorage.setItem(ADMIN_LAST_ACTIVITY_KEY, String(Date.now()))
}

function getSessionExpiresAt(): number | null {
  try {
    const raw = localStorage.getItem(ADMIN_SESSION_KEY)
    if (!raw) return null
    const meta: AdminSessionMeta = JSON.parse(raw)
    return new Date(meta.expiresAt).getTime()
  } catch {
    return null
  }
}

// ── Valid admin roles ───────────────────────────────────────────────────────

const VALID_ADMIN_ROLES: AdminRole[] = ['super_admin', 'admin', 'content_manager', 'cs_agent', 'viewer']

// ── Guard Component ─────────────────────────────────────────────────────────

interface AdminGuardProps {
  children: ReactNode
}

export default function AdminGuard({ children }: AdminGuardProps) {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null)
  const [checking, setChecking] = useState(true)
  const [loginError, setLoginError] = useState('')
  const [loading, setLoading] = useState(false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [usernameFocused, setUsernameFocused] = useState(false)
  const [passwordFocused, setPasswordFocused] = useState(false)

  const [expiryWarning, setExpiryWarning] = useState(false)

  // Check existing Supabase Auth session on mount
  useEffect(() => {
    async function checkSession() {
      if (isSupabaseConfigured()) {
        // Check Supabase Auth session
        const { data: { session } } = await supabase.auth.getSession()
        const meta = getStoredSessionMeta()

        if (session && meta) {
          // Verify admin role by fetching profile
          const { data: profile } = await supabase
            .from('profiles')
            .select('id, role, real_name, nickname, email')
            .eq('id', session.user.id)
            .single()

          if (profile && VALID_ADMIN_ROLES.includes(profile.role as AdminRole)) {
            const user: AdminUser = {
              id: session.user.id,
              username: profile.real_name || profile.nickname || session.user.email || 'Admin',
              role: profile.role as AdminRole,
            }
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setAdminUser(user)
          } else {
            // Profile role no longer valid — sign out
            await supabase.auth.signOut({ scope: 'local' })
            clearSession()
          }
        } else if (session && !meta) {
          // Supabase session exists but metadata expired/missing — sign out
          await supabase.auth.signOut({ scope: 'local' })
        } else {
          // No Supabase session — clear stale metadata
          clearSession()
        }
      } else {
        // Development fallback: check localStorage metadata only
        const meta = getStoredSessionMeta()
        if (meta) {
          const user: AdminUser = {
            id: 'local-admin',
            username: meta.displayName,
            role: meta.role,
          }
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setAdminUser(user)
        }
      }
      setChecking(false)
    }
    checkSession()
  }, [])

  // ── 유휴 활동 추적 ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!adminUser) return
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const
    const onActivity = () => updateLastActivity()
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }))
    return () => { events.forEach(e => window.removeEventListener(e, onActivity)) }
  }, [adminUser])

  // ── 1분 간격 세션 만료 체크 + 만료 5분 전 경고 ──────────────────────────────
  useEffect(() => {
    if (!adminUser) return
    const timer = setInterval(() => {
      // 유휴 타임아웃 체크
      const lastActivity = localStorage.getItem(ADMIN_LAST_ACTIVITY_KEY)
      if (lastActivity && Date.now() - Number(lastActivity) > IDLE_TIMEOUT) {
        if (isSupabaseConfigured()) {
          supabase.auth.signOut({ scope: 'local' })
        }
        clearSession()
        setAdminUser(null)
        setExpiryWarning(false)
        return
      }
      // 절대 만료 체크
      const expiresAt = getSessionExpiresAt()
      if (!expiresAt || expiresAt < Date.now()) {
        if (isSupabaseConfigured()) {
          supabase.auth.signOut({ scope: 'local' })
        }
        clearSession()
        setAdminUser(null)
        setExpiryWarning(false)
        return
      }
      // 만료 5분 전 경고
      const remaining = expiresAt - Date.now()
      if (remaining <= EXPIRY_WARNING_BEFORE && remaining > 0) {
        setExpiryWarning(true)
      } else {
        setExpiryWarning(false)
      }
    }, EXPIRY_CHECK_INTERVAL)
    return () => clearInterval(timer)
  }, [adminUser])

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    setLoginError('')
    setLoading(true)

    if (!username || !password) {
      setLoginError('이메일과 비밀번호를 모두 입력해 주세요.')
      setLoading(false)
      return
    }

    try {
      if (isSupabaseConfigured()) {
        // Production: Supabase Auth (email/password)
        const { data, error } = await supabase.auth.signInWithPassword({
          email: username,
          password,
        })

        if (error || !data.user) {

          setLoginError('이메일 또는 비밀번호가 올바르지 않습니다.')
          setLoading(false)
          return
        }

        // Fetch profile to verify admin role
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('id, role, real_name, nickname, email')
          .eq('id', data.user.id)
          .single()

        if (profileError || !profile) {
          await supabase.auth.signOut({ scope: 'local' })

          setLoginError('프로필 정보를 가져올 수 없습니다.')
          setLoading(false)
          return
        }

        if (!VALID_ADMIN_ROLES.includes(profile.role as AdminRole)) {
          await supabase.auth.signOut({ scope: 'local' })

          setLoginError('관리자 권한이 없습니다.')
          setLoading(false)
          return
        }

        const safeRole = profile.role as AdminRole
        const displayName = profile.real_name || profile.nickname || data.user.email || 'Admin'
        const user: AdminUser = {
          id: data.user.id,
          username: displayName,
          role: safeRole,
        }

        saveSession(displayName, safeRole)
        setAdminUser(user)
      } else if (import.meta.env.DEV) {
        // 개발 전용 fallback — 프로덕션 빌드에서는 Vite가 이 블록을 제거함 (dead code elimination)
        const envUser = import.meta.env.VITE_ADMIN_USERNAME
        const envPass = import.meta.env.VITE_ADMIN_PASSWORD
        if (!envUser || !envPass) {
          setLoginError('관리자 계정이 설정되지 않았습니다. 환경변수를 확인하세요.')
          setLoading(false)
          return
        }
        if (username !== envUser || password !== envPass) {
          setLoginError('이메일 또는 비밀번호가 올바르지 않습니다.')
          setLoading(false)
          return
        }
        const user: AdminUser = { id: 'local-admin', username: envUser, role: 'super_admin' }
        saveSession(envUser, 'super_admin')
        setAdminUser(user)
      } else {
        setLoginError('관리자 로그인은 Supabase Auth를 통해서만 가능합니다.')
        setLoading(false)
        return
      }
    } catch {
      setLoginError('네트워크 오류가 발생했습니다. 다시 시도해 주세요.')
    }

    setLoading(false)
  }

  async function handleLogout() {
    if (isSupabaseConfigured()) {
      await supabase.auth.signOut({ scope: 'local' })
    }
    clearSession()
    setAdminUser(null)
    setUsername('')
    setPassword('')
  }

  // ── Permission helpers (stable references) ──────────────────────────────
  // NOTE: useCallback must be called BEFORE any conditional returns (React Hooks rule)
  const userHasPermission = useCallback(
    (permission: string): boolean => {
      if (!adminUser) return false
      return checkPermission(adminUser.role, permission)
    },
    [adminUser],
  )

  const userCanAccessRoute = useCallback(
    (path: string): boolean => {
      if (!adminUser) return false
      const requiredPerm = ROUTE_PERMISSIONS[path]
      if (!requiredPerm) return true // no restriction defined → allow
      return checkPermission(adminUser.role, requiredPerm)
    },
    [adminUser],
  )

  // ── Loading state ─────────────────────────────────────────────────────────
  if (checking) {
    return (
      <div className="admin-root" style={{
        minHeight: '100dvh', background: 'var(--color-background)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Pretendard Variable', Pretendard, -apple-system, sans-serif",
      }}>
        <div style={{ textAlign: 'center' }}>
          <img src="/logo.png" alt="심장길" style={{ width: '48px', height: '48px', borderRadius: '16px', margin: '0 auto 12px' }} />
          <p style={{ fontSize: '13px', color: 'var(--color-text-tertiary)' }}>인증 확인 중...</p>
        </div>
      </div>
    )
  }

  // ── Authenticated admin ───────────────────────────────────────────────────
  if (adminUser) {
    return (
      <AdminAuthContext.Provider
        value={{
          user: adminUser,
          role: adminUser.role,
          isSuperAdmin: adminUser.role === 'super_admin',
          hasPermission: userHasPermission,
          canAccessRoute: userCanAccessRoute,
          logout: handleLogout,
        }}
      >
        {expiryWarning && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
            padding: '10px 20px', background: '#FFF3CD', borderBottom: '1px solid #FFE082',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
            fontSize: '13px', fontWeight: 600, color: '#856404',
            fontFamily: "'Pretendard Variable', Pretendard, -apple-system, sans-serif",
          }}>
            <span>세션이 곧 만료됩니다. 계속 사용하시려면 다시 로그인해 주세요.</span>
            <button
              onClick={handleLogout}
              style={{
                padding: '4px 12px', borderRadius: '6px', border: '1px solid #856404',
                background: 'transparent', color: '#856404', fontSize: '12px', fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              다시 로그인
            </button>
          </div>
        )}
        {children}
      </AdminAuthContext.Provider>
    )
  }

  // ── Login screen ──────────────────────────────────────────────────────────
  return (
    <div
      className="admin-root"
      style={{
        minHeight: '100dvh', background: 'var(--color-background)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
        fontFamily: "'Pretendard Variable', Pretendard, -apple-system, sans-serif",
      }}
    >
      <div style={{
        width: '100%', maxWidth: '400px', background: 'var(--color-surface)',
        borderRadius: '20px', padding: '40px 32px', boxShadow: 'var(--shadow-lg)',
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '32px' }}>
          <img src="/logo.png" alt="심장길" style={{ width: '40px', height: '40px', borderRadius: '12px', flexShrink: 0 }} />
          <div>
            <p style={{ fontSize: '17px', fontWeight: 700, color: 'var(--color-text-primary)', lineHeight: 1.2 }}>
              심장길 Admin
            </p>
            <p style={{ fontSize: '12px', color: 'var(--color-text-tertiary)', marginTop: '2px' }}>
              관리자 전용
            </p>
          </div>
        </div>

        <h1 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: '8px' }}>
          관리자 로그인
        </h1>
        <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '28px' }}>
          관리자 이메일로 로그인해 주세요.<br />
          <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
            일반 사용자 계정과는 별도입니다.
          </span>
        </p>

        <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '6px' }}>
              이메일
            </label>
            <input
              type="email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onFocus={() => setUsernameFocused(true)}
              onBlur={() => setUsernameFocused(false)}
              placeholder="admin@example.com"
              autoComplete="email"
              style={{
                width: '100%', height: '52px', padding: '0 16px',
                border: `1.5px solid ${usernameFocused ? 'var(--color-primary)' : 'var(--color-border)'}`,
                borderRadius: '12px', fontSize: '15px', color: 'var(--color-text-primary)',
                background: 'var(--color-surface)', outline: 'none',
                boxShadow: usernameFocused ? 'var(--shadow-focus)' : 'none',
                transition: 'border-color 0.2s, box-shadow 0.2s',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '6px' }}>
              비밀번호
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setPasswordFocused(true)}
              onBlur={() => setPasswordFocused(false)}
              placeholder="••••••••"
              autoComplete="current-password"
              style={{
                width: '100%', height: '52px', padding: '0 16px',
                border: `1.5px solid ${passwordFocused ? 'var(--color-primary)' : 'var(--color-border)'}`,
                borderRadius: '12px', fontSize: '15px', color: 'var(--color-text-primary)',
                background: 'var(--color-surface)', outline: 'none',
                boxShadow: passwordFocused ? 'var(--shadow-focus)' : 'none',
                transition: 'border-color 0.2s, box-shadow 0.2s',
              }}
            />
          </div>

          {loginError && (
            <p style={{
              fontSize: '13px', color: 'var(--color-danger)',
              padding: '10px 14px', background: '#FFF0F0', borderRadius: '8px',
            }}>
              {loginError}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              marginTop: '8px', width: '100%', height: '52px', borderRadius: '12px',
              border: 'none',
              background: loading ? 'var(--color-surface-container)' : 'var(--color-primary)',
              color: loading ? 'var(--color-text-tertiary)' : '#fff',
              fontSize: '16px', fontWeight: 700,
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit', transition: 'background 0.2s, opacity 0.2s',
            }}
          >
            {loading ? '확인 중...' : '로그인'}
          </button>
        </form>

        <p style={{ marginTop: '24px', fontSize: '12px', color: 'var(--color-text-tertiary)', textAlign: 'center' }}>
          관리자 전용 페이지입니다. 일반 사용자는{' '}
          <a href="https://heart-way.life" style={{ color: 'var(--color-primary)', textDecoration: 'none', fontWeight: 600 }}>
            메인 사이트로 이동하세요
          </a>
        </p>
        <p style={{ marginTop: '12px', textAlign: 'center' }}>
          <button
            type="button"
            onClick={() => {
              resetAdminData()
              window.location.reload()
            }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '12px', color: 'var(--color-text-tertiary)', fontFamily: 'inherit',
              textDecoration: 'underline',
            }}
          >
            세션 초기화
          </button>
        </p>
      </div>
    </div>
  )
}
